<template>
    <div>
        <div class="row mb-2">
            <div class="col-8">
                <div
                    class="position-relative d-flex flex-column justify-content-between align-items-center h-100 mt-2 mt-md-0"
                >
                    <div
                        :id="`timer-${goal ? goal.id : 0}`"
                        class="timer prog-circle d-flex flex-column justify-content-center align-center position-relative w-100 cursor-pointer"
                        @click="startTimer()"
                    >
                        <h3
                            class="text-white position-absolute font-weight-bold mb-0 w-100 text-center"
                        >
                            {{ displayTime }}
                            <small class="text-weight-light"></small>
                        </h3>
                    </div>
                    <div class="position-absolute" style="top: 5rem; z-index: 2;">
                        <small class="text-white font-weight-bold mr-1">hrs</small>
                        <small class="text-white font-weight-bold mr-1">min</small>
                        <small class="text-white font-weight-bold ml-1">sec</small>
                    </div>
                </div>
            </div>
            <div class="col-4 variable-content">
                <ol class="pl-1">
                    <li class="pl-0" v-for="(r, i) in results">
                        <div class="d-flex mb-1">
                            <span class="mr-1">{{ r }}</span>
                            <intensity
                                v-if="goal.parameters.track_intensity"
                                :mini="true"
                                :prompt="[iconset, resultsIntensity[i][1]]"
                            ></intensity>
                        </div>
                    </li>
                </ol>
            </div>
        </div>
        <div class="w-100 d-flex flex-column justify-content-start align-items-stretch pb-4">
            <div class="mt-3" v-if="goal.parameters.track_intensity">
                <intensity
                    :iconset="iconset"
                    :session="true"
                    @select-prompt="setIntensity($event)"
                    :selected-prompt="intensity ? intensity[1] : null"
                ></intensity>
            </div>
            <button
                @click.prevent="
                    (goal.parameters.track_intensity && intensity) ||
                    (!goal.parameters.track_intensity && !intensity)
                        ? saveTime()
                        : null
                "
                :disabled="goal.parameters.track_intensity && !intensity"
                class="btn btn-sm btn-block btn-primary text-uppercase text-white font-weight-bold mt-3"
            >
                Save this time
            </button>
        </div>
    </div>
</template>

<script>
import Intensity from "../../ui/IntensityIcon";
export default {
    name: "Duration",
    props: ["goal", "customization", "date", "notesData"],
    components: { Intensity },
    data() {
        return {
            startTime: this.moment(),
            undoTime: this.startTime,
            currentTime: this.moment(),
            pausedTime: 0,
            results: [],
            resultsIntensity: [],
            resultsSeconds: [],
            interval: null,
            running: false,
            timerMax: 60,
            progress: null,
            intensity: null,
        };
    },

    mounted() {
        this.$forceUpdate();
        this.progress = new this.ProgressBar.Circle(`#timer-${this.goal ? this.goal.id : 0}`, {
            strokeWidth: 7,
            easing: "easeInOut",
            duration: 1,
            color: "#b7dbf0",
            trailColor: "#50a3d7",
            trailWidth: 7,
            svgStyle: null,
        });
    },

    computed: {
        iconset() {
            return this.customization &&
                this.customization.hasOwnProperty("default_options") &&
                this.customization.default_options &&
                !isNaN(this.customization.default_options.iconset)
                ? this.customization.default_options.iconset
                : 0;
        },
        elapsedTime() {
            return Math.max(this.pausedTime, this.currentTime.diff(this.startTime, "seconds")) || 0;
        },
        displayTime() {
            return this.running
                ? this.moment()
                      .startOf("day")
                      .seconds(this.elapsedTime || 0)
                      .format("H:mm:ss")
                : this.moment()
                      .startOf("day")
                      .seconds(this.pausedTime || 0)
                      .format("H:mm:ss");
        },
    },

    methods: {
        startTimer() {
            if (!this.running) {
                if (this.pausedTime) {
                    this.startTime = this.moment().subtract(this.pausedTime, "s");
                } else {
                    this.startTime = this.moment();
                }
                this.running = true;
                this.undoTime = this.startTime;
                this.interval = setInterval(this.animate, 1000);
            } else {
                this.running = false;
                clearInterval(this.interval);
                this.pausedTime = this.elapsedTime;
            }
        },

        stopTimer() {
            this.running = false;
            clearInterval(this.interval);
            this.progress.animate(null);
        },

        setIntensity(event) {
            this.intensity = event;
        },

        saveTime() {
            if (this.goal.parameters.track_intensity && this.intensity) {
                let intensity = JSON.parse(JSON.stringify(this.intensity));
                this.resultsIntensity.push(intensity);
                this.intensity = null;
            }
            let result = this.moment()
                .startOf("day")
                .seconds(this.elapsedTime || 0)
                .format("H:mm:ss");
            this.resultsSeconds.push(this.elapsedTime);
            this.results.push(result);
            this.pausedTime = 0;
            this.running = false;
            this.stopTimer();
            this.startTime = this.moment();
            this.undoTime = this.startTime;
        },

        undoAttempt() {
            this.results.pop();
            this.resultsSeconds.pop();
            this.resultsIntensity.pop();
            this.startTime = this.undoTime;
        },

        animate() {
            this.currentTime = this.moment();
            this.progress.animate((this.elapsedTime % this.timerMax) / this.timerMax);
        },

        saveTrackerData() {
            this.stopTimer();
            let success = 0;
            if (this.resultsSeconds.length) {
                switch (this.goal.parameters.evaluate) {
                    case "average":
                        success =
                            Math.round(
                                (10 * this.resultsSeconds.reduce((a, b) => a + b, 0)) /
                                    this.resultsSeconds.length
                            ) / 10;
                        break;
                    case "max":
                    default:
                        success = this.resultsSeconds.reduce((a, b) => Math.max(a, b));
                }
            }
            const data = {
                goal_id: this.goal.id,
                data: {
                    type: "duration",
                    success,
                    results: this.resultsSeconds,
                },
                notes: this.notesData,
                session_at: this.date
                    ? this.moment(this.date).format("YYYY-MM-DD HH:mm:ss")
                    : this.moment().format("YYYY-MM-DD HH:mm:ss"),
            };
            if (this.resultsSeconds.length) {
                switch (this.goal.parameters.evaluate) {
                    case "average":
                        success =
                            Math.round(
                                (10 * this.resultsSeconds.reduce((a, b) => a + b, 0)) /
                                    this.resultsSeconds.length
                            ) / 10;
                        break;
                    case "max":
                    default:
                        success = this.resultsSeconds.reduce((a, b) => Math.max(a, b));
                }
            }
            if (this.goal.parameters.track_intensity) {
                data.data.iconset = this.iconset;
                data.data.intensity = JSON.parse(
                    JSON.stringify(this.resultsIntensity.map(intensity => intensity[0]))
                );
            }
            axios
                .post(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`, data)
                .then(r => {
                    this.reportResult(true);
                })
                .catch(error => {
                    this.$emit("save-draft", { error, data });
                    this.reportResult(false);
                    this.$swal({
                        title: "Error!",
                        text: "An error occurred while saving.",
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });
                    console.error(error);
                });
        },

        reportResult(success) {
            this.$emit("saved", success);
            if (success) {
                let longest = this.resultsSeconds.length
                    ? this.resultsSeconds.reduce((a, b) => Math.max(a, b))
                    : 0;
                let average = this.resultsSeconds.length
                    ? Math.round(
                          this.resultsSeconds.reduce((a, b) => a + b) / this.resultsSeconds.length
                      )
                    : 0;
                this.$emit(
                    "close-tracker",
                    this.goal.title +
                        ": " +
                        `Longest interval : ${longest} seconds, average ${average} seconds` +
                        " - " +
                        this.notesData
                );
            }
        },
    },
};
</script>

<style scoped lang="scss">
.timer {
    min-width: 150px;
    max-width: 150px;
    background-color: #3da2d8;
    box-shadow: inset 0 0 0 13px white;
    border-radius: 50%;
    min-height: 150px;
    max-height: 150px;
}

.prog-circle > h5 {
    font-family: "Roboto Condensed", sans-serif;
}
</style>
